import * as XLSX from "xlsx";
import { cleanAndFilterSpreadsheetData } from "./spreadsheet";

/**
 * Isomorphic XLSX → CSV conversion. Lives here (rather than in the backend) so
 * the browser can convert an uploaded workbook before it is stored.
 *
 * Intentionally NOT re-exported from `src/index.ts`: SheetJS is ~1MB, and the
 * barrel is imported by nearly every workspace. Import the subpath directly:
 * `import { xlsxWorkbookToCsv } from "@erp/shared-utils/src/xlsx-to-csv"`.
 *
 * Node-only helpers (buffer + streaming variants) live in
 * `backend/src/core/services/documents/xlsxToCsv.ts`.
 */
export interface XlsxToCsvOptions {
  /**
   * Sheet to extract — name (string) or index (number). Defaults to the first
   * sheet. Throws if a named/index sheet is missing.
   */
  sheet?: string | number;
  /**
   * When true (default), runs `cleanAndFilterSpreadsheetData` over the raw
   * rows: drops empty rows/columns and normalizes formatted numbers. Pass
   * `false` to preserve the exact sheet layout (e.g. when a downstream AI
   * needs to see banner rows / boundaries verbatim).
   */
  clean?: boolean;
}

/**
 * Convert one sheet of an already-parsed XLSX workbook to CSV.
 *
 * Prefer this over `xlsxBufferToCsv` in loops — it lets the caller parse the
 * workbook once and convert N sheets without re-reading the buffer each time.
 */
export function xlsxWorkbookToCsv(
  workbook: XLSX.WorkBook,
  options: XlsxToCsvOptions = {},
): string {
  const { sheet: sheetSelector, clean = true } = options;

  if (workbook.SheetNames.length === 0) {
    throw new Error("XLSX has no sheets");
  }

  let sheetName: string;
  if (typeof sheetSelector === "number") {
    const candidate = workbook.SheetNames[sheetSelector];
    if (!candidate) {
      throw new Error(
        `XLSX sheet index ${sheetSelector} out of range (have ${workbook.SheetNames.length} sheets)`,
      );
    }
    sheetName = candidate;
  } else if (typeof sheetSelector === "string") {
    if (!workbook.Sheets[sheetSelector]) {
      throw new Error(`XLSX sheet "${sheetSelector}" not found`);
    }
    sheetName = sheetSelector;
  } else {
    const [firstSheetName] = workbook.SheetNames;
    if (!firstSheetName) {
      throw new Error("XLSX has no sheets");
    }
    sheetName = firstSheetName;
  }

  const sheet = workbook.Sheets[sheetName];
  if (!sheet) {
    throw new Error(`XLSX sheet "${sheetName}" not found`);
  }

  if (!clean) {
    return XLSX.utils.sheet_to_csv(sheet);
  }

  const rawData = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: "",
    raw: false,
  }) as unknown[][];
  const cleaned = cleanAndFilterSpreadsheetData(rawData);
  return XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet(cleaned));
}
